/**
 * External Imports
 */
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

/**
 * Internal Imports
 */
import CNEdListTable from '../../../components/customTables/support/cnedListTable';
import SearchHeader from '../../../components/searchHeader';
import SupportSideNav from '../../../components/sideNav/SupportSideNav';

import { RootState } from '../../../redux/store';
import { useGetSupportSubjectsToRoundQuery } from '../../../redux/apis/Users.api';

import { date } from '../../../utils/helpers';
/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Employee,
  RoundHistory
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { SupportContainer } from '../styles/Support.styles';

export interface CNEdListProps {
  authorizedUser?: AuthUserProps;
  selectedDeptsFromStore: string[];
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const CNEdList: FC<CNEdListProps> = ({
  authorizedUser,
  selectedDeptsFromStore,
  searchTerm,
  setSearchTerm
}) => {
  const { cnedFilter = 'all' } = useParams();

  const {
    data: cnedSubjectsToRoundData,
    // isError: isGetSupportSubjectsToRoundError,
    // isFetching: isGetSupportSubjectsToRoundFetching,
    isLoading: isGetSupportSubjectsToRoundLoading
  } = useGetSupportSubjectsToRoundQuery(
    {
      facilityId: authorizedUser?.facilityId ?? '',
      hcaid: authorizedUser?.hcaid ?? '',
      type: 'cned'
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !(authorizedUser?.hcaid && authorizedUser?.facilityId)
    }
  );

  const filteredCNEdRounds = useMemo(() => {
    const filteredByDept =
      cnedSubjectsToRoundData?.filter(employee => {
        const employeeDept =
          (employee.adHoc ? employee?.employeeDept : employee?.dept) ||
          'Unknown';
        return selectedDeptsFromStore.some(
          selectedDept => selectedDept === employeeDept
        );
      }) ?? [];

    return filteredByDept.reduce(
      (
        acc: {
          cnedSubjects: (Employee & RoundHistory)[];
          adHoc: (Employee & RoundHistory)[];
        },
        employee: Employee & RoundHistory
      ) =>
        employee.adHoc
          ? { ...acc, adHoc: [...acc.adHoc, employee] }
          : { ...acc, cnedSubjects: [...acc.cnedSubjects, employee] },
      { cnedSubjects: [], adHoc: [] }
    );
  }, [cnedSubjectsToRoundData, selectedDeptsFromStore]);

  const noRoundCount = useMemo(() => {
    return filteredCNEdRounds.cnedSubjects.filter(
      employee =>
        !employee?.lastRoundDate ||
        (employee?.lastRoundDate &&
          date.isOverInDays(employee.lastRoundDate, 30))
    ).length;
  }, [filteredCNEdRounds]);

  const recentlyCompletedCount = useMemo(() => {
    return (
      filteredCNEdRounds.cnedSubjects.filter(
        employee =>
          employee?.lastRoundDate &&
          !date.isOverInWeeks(employee.lastRoundDate, 2)
      ).length +
      filteredCNEdRounds.adHoc.filter(
        employee =>
          employee?.lastRoundDate &&
          !date.isOverInWeeks(employee.lastRoundDate, 2)
      ).length
    );
  }, [filteredCNEdRounds]);

  const newNursesCount = useMemo(() => {
    return filteredCNEdRounds.cnedSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewNurse'
      )
    ).length;
  }, [filteredCNEdRounds]);

  const newToHCACount = useMemo(() => {
    return filteredCNEdRounds.cnedSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewToHCA'
      )
    ).length;
  }, [filteredCNEdRounds]);

  const newToDeptCount = useMemo(() => {
    return filteredCNEdRounds.cnedSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewToDepartment'
      )
    ).length;
  }, [filteredCNEdRounds]);

  return (
    <SupportContainer data-testid="cned-support-view">
      <SupportSideNav
        type="cned"
        total={noRoundCount + recentlyCompletedCount}
        noRoundCount={noRoundCount}
        recentlyCompletedCount={recentlyCompletedCount}
        newNursesCount={newNursesCount}
        newToHCACount={newToHCACount}
        newToDeptCount={newToDeptCount}
      />
      <div className="d-flex flex-column w-100">
        <SearchHeader searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        <CNEdListTable
          adhocRounds={filteredCNEdRounds.adHoc}
          cnedFilter={cnedFilter}
          cnedRounds={filteredCNEdRounds.cnedSubjects}
          isLoading={isGetSupportSubjectsToRoundLoading}
          searchTerm={searchTerm}
        />
      </div>
    </SupportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    selectedDeptsFromStore: state.EmployeeReducer.selectedDepts
  };
};

export default connect(mapReduxStateToProps)(CNEdList);
